/* eslint @typescript-eslint/no-explicit-any: 0 */
import dayjs from 'dayjs'
import locale from './locale'
import { DateTimeFormatType, MomentFormatType, NumberFormatType, StringFormatType } from './types'

function formatRelativeTime(value: Date) {
	const relativeTimeFormatter = new Intl.RelativeTimeFormat(locale.getCurrentHierarchy())
	const timeDelta = Date.now() - value.getTime()
	const minute = 60000
	const hour = minute * 60
	const day = hour * 24
	if (timeDelta <= hour) {
		const minutes = Math.ceil(timeDelta / minute)
		return relativeTimeFormatter.format(-minutes, 'minute')
	}
	if (timeDelta <= day) {
		const hours = Math.floor(timeDelta / hour)
		return relativeTimeFormatter.format(-hours, 'hour')
	}
	const days = Math.floor(timeDelta / day)
	return relativeTimeFormatter.format(-days, 'day')
}

function formatDateTime(value: Date, formatType?: DateTimeFormatType | MomentFormatType) {
	if (formatType === 'relative') {
		return formatRelativeTime(value)
	} else {
		const { date, time, format: momentFormat } = formatType as Record<string, string>

		if (typeof momentFormat === 'string') {
			return dayjs(value).format(momentFormat)
		} else {
			return new Intl.DateTimeFormat(locale.getCurrentHierarchy(), {
				dateStyle: date,
				timeStyle: time,
			} as any).format(value)
		}
	}
}

function formatNumber(value: number, formatType?: NumberFormatType) {
	const minimumFractionDigits = Number.isInteger(value)
		? 0
		: formatType?.decimals instanceof Array
		? formatType.decimals[0]
		: formatType?.decimals
	const maximumFractionDigits = formatType?.decimals instanceof Array ? formatType.decimals[1] : formatType?.decimals

	// force format looking in case currency is presented
	return new Intl.NumberFormat(locale.getCurrentHierarchy(), {
		minimumFractionDigits,
		maximumFractionDigits,
		...(formatType?.currency && {
			style: 'currency',
			currency: formatType?.currency,
		}),
	}).format(value)
}

function formatString(value: string, formatType?: StringFormatType) {
	if (formatType === 'lowercase') {
		return value.toLowerCase()
	} else if (formatType === 'uppercase') {
		return value.toUpperCase()
	} else if (formatType === 'capitalize') {
		return value.length > 0 ? `${value[0].toUpperCase()}${value.slice(1)}` : value
	} else {
		return value
	}
}

function format(value: Date, formatType?: DateTimeFormatType): string
function format(value: Date, formatType?: MomentFormatType): string
function format(value: number, formatType?: NumberFormatType): string
function format(value: string, formatType?: StringFormatType): string
function format(value: unknown, formatType?: unknown): string {
	if (value instanceof Date) {
		return formatDateTime(value, formatType as DateTimeFormatType | MomentFormatType)
	} else if (typeof value === 'number') {
		return formatNumber(value, formatType as NumberFormatType)
	} else if (typeof value === 'string') {
		return formatString(value, formatType as StringFormatType)
	} else {
		return String(value)
	}
}

export function formatCurrency(value: number, currency?: string) {
	return `${formatNumber(value, {
		decimals: [0, 12],
	})} ${currency}`
}

export default format
